import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { PageHeader } from "../components/PageHeader";
import { Sparkle, PaperPlaneRight, User, ClipboardText, X } from "@phosphor-icons/react";

const SUGGESTED_PROMPTS = [
  "Riassumi le mie pratiche prioritarie",
  "Scrivi una email di sollecito per una fattura non pagata",
  "Suggeriscimi un testo per un preventivo di perizia",
  "Cosa devo fare oggi con priorità alta?",
];

export default function AIAssistant() {
  const [searchParams, setSearchParams] = useSearchParams();
  const praticaId = searchParams.get("pratica_id");

  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ciao! Sono il tuo assistente. Posso aiutarti a organizzare le pratiche, scrivere email professionali, riassumere scadenze o suggerirti priorità. Come posso esserti utile?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [context, setContext] = useState("");
  const [contextPratica, setContextPratica] = useState(null);
  const scrollRef = useRef(null);

  // Carica contesto pratica dai query param
  useEffect(() => {
    if (!praticaId) { setContext(""); setContextPratica(null); return; }
    api.get(`/pratiche/${praticaId}`).then((r) => {
      const p = r.data;
      const parts = [];
      parts.push(`PRATICA CORRENTE: ${p.titolo || ""}`);
      if (p.tipo) parts.push(`Tipo: ${p.tipo}`);
      if (p.stato) parts.push(`Stato: ${p.stato}`);
      if (p.priorita) parts.push(`Priorità: ${p.priorita}`);
      if (p.indirizzo_immobile) parts.push(`Immobile: ${p.indirizzo_immobile}${p.citta_immobile ? " - " + p.citta_immobile : ""}`);
      if (p.dati_catastali) parts.push(`Catasto: ${p.dati_catastali}`);
      if (p.descrizione) parts.push(`Descrizione: ${p.descrizione}`);
      if (p.scadenza) parts.push(`Scadenza: ${p.scadenza}`);
      const fasi = (p.fasi || []).filter(f => !f.completata).slice(0, 5).map(f => f.nome).join(", ");
      if (fasi) parts.push(`Fasi aperte: ${fasi}`);
      setContext(parts.join("\n"));
      setContextPratica({ id: p.id, titolo: p.titolo, tipo: p.tipo, citta: p.citta_immobile });
    }).catch(() => {});
  }, [praticaId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const rimuoviContesto = () => {
    setContext("");
    setContextPratica(null);
    searchParams.delete("pratica_id");
    setSearchParams(searchParams);
    setSessionId(null);  // reset session (nuovo contesto → nuova conversazione)
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId, context });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", content: data.response }]);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore AI";
      toast.error(msg);
      setMessages(m => [...m, { role: "assistant", content: "⚠️ Errore di connessione all'assistente. Riprova." }]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <div className="p-6 md:p-8 w-full max-w-[900px] mx-auto" data-testid="ai-page">
      <PageHeader
        eyebrow="Intelligenza Artificiale"
        title="Assistente AI"
        description="Esperto di normativa edilizia italiana e Regione Toscana. Conosce le tue pratiche aperte."
      />

      {contextPratica && (
        <Card className="border-blue-200 bg-blue-50 shadow-none mb-4 p-3 flex items-center justify-between" data-testid="ai-context-pratica">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-md bg-blue-900 text-white flex items-center justify-center flex-shrink-0">
              <ClipboardText size={16} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 mb-0.5">Contesto attivo</div>
              <div className="text-sm font-semibold text-blue-900 truncate">{contextPratica.titolo}</div>
              <div className="text-[11px] font-mono text-blue-700">{contextPratica.tipo}{contextPratica.citta ? ` · ${contextPratica.citta}` : ""}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={rimuoviContesto}
            className="text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-md p-1.5 transition-colors flex-shrink-0"
            title="Rimuovi contesto"
            data-testid="ai-context-remove"
          >
            <X size={16} />
          </button>
        </Card>
      )}

      <Card className="border-slate-200 shadow-none flex flex-col h-[calc(100vh-280px)] min-h-[500px]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${i}`}>
              {m.role === "assistant" && (
                <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                  <Sparkle size={16} weight="regular" />
                </div>
              )}
              <div className={`max-w-[75%] rounded-md px-4 py-3 text-sm ${
                m.role === "user"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-50 border border-slate-200 text-slate-800"
              }`}>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              </div>
              {m.role === "user" && (
                <div className="h-8 w-8 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                  <User size={16} weight="regular" className="text-slate-700" />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center"><Sparkle size={16} /></div>
              <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm text-slate-500">
                <span className="inline-flex gap-1">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="px-6 pb-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {SUGGESTED_PROMPTS.map((p, i) => (
              <button
                key={i}
                onClick={() => send(p)}
                disabled={loading}
                data-testid={`prompt-${i}`}
                className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200">
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Scrivi un messaggio..."
              rows={1}
              className="resize-none min-h-[44px] max-h-[120px]"
              disabled={loading}
              data-testid="ai-input"
            />
            <Button type="submit" disabled={loading || !input.trim()} data-testid="ai-send-button">
              <PaperPlaneRight size={16} weight="bold" />
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
